<template>
    <div class="planejamento-semanal">
      <v-card class="mb-4" elevation="2">
        <v-card-title class="d-flex align-center bg-gradient-success text-white">
          <v-icon class="mr-2">mdi-calendar-week</v-icon>
          <div>
            <div class="text-h6">Planejamento Semanal de Produção</div>
            <div class="text-subtitle-2 opacity-90">
              Organize as ordens por semana de produção
            </div>
          </div>
  
   
  
          <v-spacer />
          <div class="d-flex align-center gap-2">
            <v-btn icon variant="text" color="white" @click="semanaAnterior" :disabled="!podeVoltarSemana">
              <v-icon>mdi-chevron-left</v-icon>
            </v-btn>
            <v-chip color="white" text-color="success" size="large" class="font-weight-bold">
              {{ formatarPeriodoSemana }}
            </v-chip>
            <v-btn icon variant="text" color="white" @click="proximaSemana">
              <v-icon>mdi-chevron-right</v-icon>
            </v-btn>
          </div>
        </v-card-title>
      </v-card>
  
      <div v-if="carregando" class="loading-container">
        <v-progress-circular indeterminate color="primary" size="64" />
        <div class="mt-4 text-h6">⏳ Carregando dados do planejamento...</div>
      </div>
  
      <!-- Conteúdo Principal -->
      <div v-else>
        <!-- Estatísticas da Semana -->
        <v-row class="mb-4">
          <v-col cols="12" md="3">
            <v-card variant="outlined" class="text-center pa-4">
              <div class="text-h4 primary--text">{{ totalOrdensAlocadas }}</div>
              <div class="text-caption">Ordens Alocadas</div>
            </v-card>
          </v-col>
          <v-col cols="12" md="3">
            <v-card variant="outlined" class="text-center pa-4">
              <div class="text-h4 success--text">{{ capacidadeUtilizada }}%</div>
              <div class="text-caption">Capacidade Utilizada</div>
            </v-card>
          </v-col>
          <v-col cols="12" md="3">
            <v-card variant="outlined" class="text-center pa-4">
              <div class="text-h4 warning--text">{{ ordensUrgentes }}</div>
              <div class="text-caption">Ordens Urgentes</div>
            </v-card>
          </v-col>
          <v-col cols="12" md="3">
            <v-card variant="outlined" class="text-center pa-4">
              <div class="text-h4 error--text">{{ conflitosDetectados }}</div>
              <div class="text-caption">Conflitos</div>
            </v-card>
          </v-col>
        </v-row>
  
        <!-- Grid de Planejamento -->
        <v-row>
          <!-- Coluna de Ordens Disponíveis -->
          <v-col cols="12" lg="4">
            <v-card elevation="2" class="h-100">
              <v-card-title class="bg-grey-lighten-4 d-flex align-center">
                <v-icon class="mr-2">mdi-clipboard-list</v-icon>
                Ordens Disponíveis
                <v-spacer />
                <v-chip size="small" color="primary">{{ ordensDisponiveis.length }}</v-chip>
              </v-card-title>
  
              <!-- Filtros para ordens disponíveis -->
              <v-card-subtitle class="pa-3 pb-0">
                <div class="d-flex gap-2">
                  <v-text-field v-model="filtroOrdens" label="Filtrar ordens..." prepend-inner-icon="mdi-magnify"
                    variant="outlined" density="compact" clearable hide-details class="flex-grow-1" />
                  <v-btn @click="abrirFiltroAvancado" variant="outlined" density="compact" 
                    color="primary" icon="mdi-filter-variant" />
                </div>
              </v-card-subtitle>
  
              <v-card-text class="pa-2 ordens-disponiveis-container">
                <!-- Drag and Drop das ordens disponíveis -->
                <draggable v-model="ordensDisponiveisFiltradas" :group="{ name: 'ordens', pull: 'clone', put: false }"
                  :clone="clonarOrdem" class="ordens-disponiveis" :sort="false" itemKey="id">
                  <template #item="{ element: ordem }">
                    <OrdemMiniCard :ordem="ordem" :draggable="true" class="mb-2" />
                  </template>
                </draggable>
  
                <!-- Estado vazio -->
                <div v-if="ordensDisponiveisFiltradas.length === 0" class="text-center pa-4">
                  <v-icon size="48" color="grey">mdi-clipboard-search</v-icon>
                  <div class="text-body-2 grey--text mt-2">
                    {{ filtroOrdens ? 'Nenhuma ordem encontrada' : 'Todas as ordens foram alocadas' }}
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
  
          <!-- Coluna do Planejamento Semanal -->
          <v-col cols="12" lg="8">
            <v-card elevation="2">
              <v-card-title class="bg-primary text-white d-flex align-center">
                <v-icon class="mr-2">mdi-calendar-range</v-icon>
                <div>
                  <div class="text-h6">{{ formatarPeriodoSemana }}</div>
                  <div class="text-subtitle-2 opacity-90">
                    Semana {{ numeroSemana }} de {{ anoAtual }}
                  </div>
                </div>
                <v-spacer />
                <v-btn color="white" variant="outlined" @click="salvarPlanejamento" :loading="salvandoPlanejamento"
                  :disabled="!temAlteracoes">
                  <v-icon left>mdi-content-save</v-icon>
                  Salvar Planejamento
                </v-btn>
              </v-card-title>
  
              <v-card-text class="pa-0">
                <!-- Dias da Semana -->
                <div class="semana-grid">
                  <div v-for="(dia, index) in diasSemana" :key="index" class="dia-coluna" :class="{
                    'dia-hoje': ehHoje(dia.data),
                    'dia-fim-semana': ehFimDeSemana(dia.data)
                  }">
                    <!-- Header do Dia -->
                    <div class="dia-header">
                      <div class="text-subtitle-2 font-weight-bold">
                        {{ dia.nome }}
                      </div>
                      <div class="text-caption">
                        {{ formatarDia(dia.data) }}
                      </div>
                      <div class="mt-1 d-flex align-center justify-center gap-1">
                        <v-chip v-if="dia.ordens.length > 0" size="x-small" color="primary">
                          {{ dia.ordens.length }}
                        </v-chip>
                        <v-chip v-if="calcularCapacidadeDia(dia) > 100" size="x-small" color="error">
                          {{ calcularCapacidadeDia(dia) }}%
                        </v-chip>
                      </div>
                    </div>
  
                    <!-- Área de Drop para Ordens -->
                    <div class="dia-conteudo">
                      <!-- ✅ SINTAXE NOVA: -->
                      <draggable v-model="dia.ordens" :group="{ name: 'ordens' }" class="drop-zone" :class="{
                        'drop-zone-empty': dia.ordens.length === 0,
                        'drop-zone-over-capacity': calcularCapacidadeDia(dia) > 100
                      }" @add="onOrdemAdicionada($event, dia)" @remove="onOrdemRemovida($event, dia)"
                        @change="onPlanejamentoAlterado" itemKey="id">
                        <template #item="{ element: ordem }">
                          <OrdemPlanejamentoCard :ordem="ordem" :dia="dia" @remover="removerOrdemDoDia"
                            class="mb-2" />
                        </template>
  
                        <template #footer>
                          <div v-if="dia.ordens.length === 0" class="drop-placeholder">
                            <v-icon size="32" color="grey-lighten-2">
                              mdi-plus-circle-outline
                            </v-icon>
                            <div class="text-caption grey--text">
                              Arraste ordens aqui
                            </div>
                          </div>
                        </template>
                      </draggable>
                    </div>
  
                    <!-- Footer do Dia com Capacidade -->
                    <div class="dia-footer">
                      <v-progress-linear :model-value="Math.min(100, calcularCapacidadeDia(dia))"
                        :color="getCorCapacidade(calcularCapacidadeDia(dia))" height="4" rounded />
                      <div class="text-caption text-center mt-1">
                        {{ calcularCapacidadeDia(dia) }}% capacidade
                      </div>
                    </div>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
  
        <!-- Alertas e Conflitos -->
        <v-card v-if="alertasPlanejamento.length > 0" class="mt-4" elevation="2">
          <v-card-title class="bg-warning text-white">
            <v-icon class="mr-2">mdi-alert</v-icon>
            Alertas do Planejamento
          </v-card-title>
          <v-card-text>
            <v-list>
              <v-list-item v-for="(alerta, index) in alertasPlanejamento" :key="index">
                <template v-slot:prepend>
                  <v-icon :color="alerta.cor">{{ alerta.icone }}</v-icon>
                </template>
                <v-list-item-title>{{ alerta.titulo }}</v-list-item-title>
                <v-list-item-subtitle>{{ alerta.descricao }}</v-list-item-subtitle>
              </v-list-item>
            </v-list>
          </v-card-text>
        </v-card>
      </div>

      <!-- Dialog de Filtro Avançado -->
      <v-dialog v-model="dialogFiltroAvancado" fullscreen persistent>
      <v-card class="filtros-dialog-card">
        <v-card-title class="d-flex align-center bg-primary text-white pa-4">
          <v-icon class="mr-2">mdi-filter-variant</v-icon>
          <span class="text-h5">Filtros Avançados</span>
          <v-spacer />
          <v-btn icon="mdi-close" variant="text" color="white" @click="fecharFiltroAvancado" />
        </v-card-title>

        <v-card-text class="pa-6">
          <div class="filtros-container">
            <!-- Filtro por Cliente -->
            <div class="filtro-item mb-4">
              <h3 class="filtro-titulo mb-2">
                <v-icon class="mr-2" color="primary">mdi-account-group</v-icon>
                Clientes
              </h3>
              <v-select
                v-model="filtrosAvancados.clientes"
                :items="opcoesClientes"
                label="Digite para buscar clientes"
                multiple
                chips
                closable-chips
                variant="outlined"
                density="comfortable"
                item-title="text"
                item-value="value"
                :menu-props="{ maxHeight: 300 }"
                clearable
                :custom-filter="filtrarOpcoes"
              >
                <template #prepend-item>
                  <v-list-item @click="selecionarTodosClientes">
                    <template #prepend>
                      <v-checkbox :model-value="todosSelecionados.clientes" />
                    </template>
                    <v-list-item-title>Selecionar Todos os Clientes</v-list-item-title>
                  </v-list-item>
                  <v-divider />
                </template>
                <template #selection="{ item, index }">
                  <v-chip v-if="index < 3" color="primary" variant="outlined" closable @click:close="removerCliente(item.value)">
                    {{ item.title }}
                  </v-chip>
                  <span v-if="index === 3" class="text-grey text-caption align-self-center">
                    (+{{ filtrosAvancados.clientes.length - 3 }} outros)
                  </span>
                </template>
              </v-select>
            </div>

            <!-- Filtro por Modelo -->
            <div class="filtro-item mb-4">
              <h3 class="filtro-titulo mb-2">
                <v-icon class="mr-2" color="primary">mdi-package-variant</v-icon>
                Modelos/Produtos
              </h3>
              <v-select
                v-model="filtrosAvancados.modelos"
                :items="opcoesModelos"
                label="Digite para buscar produtos"
                multiple
                chips
                closable-chips
                variant="outlined"
                density="comfortable"
                item-title="text"
                item-value="value"
                :menu-props="{ maxHeight: 300 }"
                clearable
                :custom-filter="filtrarOpcoes"
              >
                <template #prepend-item>
                  <v-list-item @click="selecionarTodosModelos">
                    <template #prepend>
                      <v-checkbox :model-value="todosSelecionados.modelos" />
                    </template>
                    <v-list-item-title>Selecionar Todos os Produtos</v-list-item-title>
                  </v-list-item>
                  <v-divider />
                </template>
                <template #selection="{ item, index }">
                  <v-chip v-if="index < 2" color="success" variant="outlined" closable @click:close="removerModelo(item.value)">
                    {{ item.title }}
                  </v-chip>
                  <span v-if="index === 2" class="text-grey text-caption align-self-center">
                    (+{{ filtrosAvancados.modelos.length - 2 }} outros)
                  </span>
                </template>
              </v-select>
            </div>

            <!-- Filtro por Setor -->
            <div class="filtro-item mb-4">
              <h3 class="filtro-titulo mb-2">
                <v-icon class="mr-2" color="primary">mdi-factory</v-icon>
                Setores
              </h3>
              <v-select
                v-model="filtrosAvancados.setores"
                :items="opcoesSetores"
                label="Digite para buscar setores"
                multiple
                chips
                closable-chips
                variant="outlined"
                density="comfortable"
                item-title="text"
                item-value="value"
                :menu-props="{ maxHeight: 300 }"
                clearable
                :custom-filter="filtrarOpcoes"
              >
                <template #prepend-item>
                  <v-list-item @click="selecionarTodosSetores">
                    <template #prepend>
                      <v-checkbox :model-value="todosSelecionados.setores" />
                    </template>
                    <v-list-item-title>Selecionar Todos os Setores</v-list-item-title>
                  </v-list-item>
                  <v-divider />
                </template>
                <template #selection="{ item, index }">
                  <v-chip v-if="index < 4" color="warning" variant="outlined" closable @click:close="removerSetor(item.value)">
                    {{ item.title }}
                  </v-chip>
                  <span v-if="index === 4" class="text-grey text-caption align-self-center">
                    (+{{ filtrosAvancados.setores.length - 4 }} outros)
                  </span>
                </template>
              </v-select>
            </div>

            <!-- Filtro por ID Pedido -->
            <div class="filtro-item mb-4">
              <h3 class="filtro-titulo mb-2">
                <v-icon class="mr-2" color="primary">mdi-file-document</v-icon>
                IDs de Pedido
              </h3>
              <v-select
                v-model="filtrosAvancados.pedidos"
                :items="opcoesPedidos"
                label="Digite para buscar pedidos"
                multiple
                chips
                closable-chips
                variant="outlined"
                density="comfortable"
                item-title="text"
                item-value="value"
                :menu-props="{ maxHeight: 300 }"
                clearable
                :custom-filter="filtrarOpcoes"
              >
                <template #prepend-item>
                  <v-list-item @click="selecionarTodosPedidos">
                    <template #prepend>
                      <v-checkbox :model-value="todosSelecionados.pedidos" />
                    </template>
                    <v-list-item-title>Selecionar Todos os Pedidos</v-list-item-title>
                  </v-list-item>
                  <v-divider />
                </template>
                <template #selection="{ item, index }">
                  <v-chip v-if="index < 5" color="info" variant="outlined" closable @click:close="removerPedido(item.value)">
                    {{ item.title }}
                  </v-chip>
                  <span v-if="index === 5" class="text-grey text-caption align-self-center">
                    (+{{ filtrosAvancados.pedidos.length - 5 }} outros)
                  </span>
                </template>
              </v-select>
            </div>
          </div>

          <!-- Resumo dos filtros -->
          <v-row v-if="temFiltrosAtivos" class="mt-2">
            <v-col cols="12">
              <v-alert type="info" variant="tonal" density="compact">
                <strong>{{ ordensFiltradasCount }}</strong> ordens encontradas com os filtros aplicados
              </v-alert>
            </v-col>
          </v-row>
        </v-card-text>

        <v-card-actions class="pa-4">
          <v-btn @click="limparFiltros" variant="outlined" color="warning">
            <v-icon left>mdi-filter-remove</v-icon>
            Limpar Filtros
          </v-btn>
          <v-spacer />
          <v-btn @click="fecharFiltroAvancado" variant="outlined">
            Cancelar
          </v-btn>
          <v-btn @click="aplicarFiltros" color="primary" variant="elevated">
            <v-icon left>mdi-check</v-icon>
            Aplicar Filtros
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

      <!-- Modal de Seleção de Setores -->
      <v-dialog v-model="modalSetores.mostrar" fullscreen persistent>
        <v-card>
          <v-card-title class="text-h5 bg-primary text-white">
            🏭 Selecionar Setores para {{ modalSetores.dia?.nome }}
          </v-card-title>
  
          <v-card-subtitle class="pa-4">
            <div class="ordem-info">
              <h3>📋 Ordem #{{ modalSetores.ordem?.id_ordem }}</h3>
              <p><strong>Cliente:</strong> {{ modalSetores.ordem?.cliente }}</p>
              <p><strong>Produto:</strong> {{ modalSetores.ordem?.produto }}</p>
              <p><strong>Quantidade Total:</strong> {{ modalSetores.ordem?.quantidadeTotal }} un</p>
            </div>
          </v-card-subtitle>
  
          <v-card-text>
            <div class="setores-selection">
              <!-- Filtros -->
              <div class="d-flex align-center justify-space-between mb-3">
                <h4 class="mb-0">🔧 Setores disponíveis para esta ordem:</h4>
  
                <div class="d-flex">
                  <v-select v-model="modalSetores.filtroStatus"
                    :items="['Todos', 'Pendentes', 'Em andamento', 'Finalizados', 'Sem saldo']" density="compact"
                    label="Filtrar por status" class="mr-2" style="min-width:150px" />
  
                  <v-btn color="primary" variant="outlined" size="small" class="ml-2" @click="selecionarTodosSetores"
                    :disabled="todosSetoresSelecionados || !setoresFiltradosDisponiveis.length">
                    <v-icon left>mdi-checkbox-multiple-marked</v-icon>
                    Selecionar todos
                  </v-btn>
                </div>
              </div>
  
              <!-- Loading -->
              <div v-if="modalSetores.carregandoSetores" class="text-center pa-4">
                <v-progress-circular indeterminate color="primary" />
                <div class="mt-2">Carregando setores da ordem...</div>
              </div>
  
              <!-- Grid de setores -->
              <div v-else>
                <div class="mb-2 text-subtitle-2">
                  <span v-if="setoresFiltradosDisponiveis.length === 0" class="text-red">
                    Nenhum setor encontrado com o filtro atual.
                  </span>
                  <span v-else>
                    Exibindo {{ setoresFiltradosDisponiveis.length }} de {{ modalSetores.setoresDisponiveis.length }}
                    setores
                  </span>
                </div>
  
                <!-- ✅ GRID DE SETORES SIMPLIFICADO -->
                <div class="setores-grid-clean">
                  <v-card v-for="setor in setoresFiltradosDisponiveis" :key="setor.id" :class="['setor-card-clean', {
                    'selected': modalSetores.setoresSelecionados.includes(setor.id),
                    'setor-disabled': !podeSelecionar(setor),
                    'setor-sem-saldo': setor.saldo_disponivel === 0
                  }]" @click="toggleSetor(setor.id, setor)" elevation="0" :disabled="!podeSelecionar(setor)">
                    <!-- ✅ HEADER SIMPLIFICADO -->
                    <div class="setor-header-clean">
                      <div class="header-main">
                        <v-checkbox :model-value="modalSetores.setoresSelecionados.includes(setor.id)"
                          @click.stop="toggleSetor(setor.id, setor)" :disabled="!podeSelecionar(setor)" color="primary"
                          hide-details density="compact" />
  
                        <div class="setor-info">
                          <h3 class="setor-titulo">{{ setor.nome }}</h3>
                          <div class="setor-tags">
                            <v-chip size="small" :color="getCorStatusSetor(setor.status_geral)" variant="flat">
                              {{ setor.status_geral || 'Pendente' }}
                            </v-chip>
                            <span class="setor-detalhes">{{ setor.parte }} • {{ setor.tamanho }}</span>
                          </div>
                        </div>
                      </div>
  
                      <div class="status-badge"
                        :class="setor.previsao_falta_a_ser_produzida > 0 ? 'disponivel' : 'indisponivel'">
                        <v-icon size="16">
                          {{ setor.previsao_falta_a_ser_produzida > 0 ? 'mdi-check-circle' : 'mdi-alert-circle' }}
                        </v-icon>
                        <span>{{ setor.previsao_falta_a_ser_produzida > 0 ? 'Disponível' : 'Sem saldo' }}</span>
                      </div>
                    </div>
  
                    <!-- ✅ CONTEÚDO SIMPLIFICADO -->
                    <div class="setor-body-clean">
                      <!-- Responsável e Tempo -->
                      <div class="info-row-clean">
                        <div class="info-item-clean">
                          <v-icon size="16" color="primary">mdi-account</v-icon>
                          <span class="info-text">{{ setor.funcionario_atual || 'Não atribuído' }}</span>
                        </div>
                        <div class="info-item-clean">
                          <v-icon size="16" color="orange">mdi-clock</v-icon>
                          <span class="info-text">{{ setor.tempo_estimado || '0h' }}</span>
                        </div>
                      </div>
  
                      <!-- ✅ MÉTRICAS PRINCIPAIS (SÓ 3) -->
                      <div class="metricas-clean">
                        <div class="metrica-clean planejada">
                          <span class="metrica-label">Planejada</span>
                          <span class="metrica-valor">{{ setor.planejada_final || 0 }}</span>
                        </div>
  
                        <div class="metrica-clean produzida">
                          <span class="metrica-label">Produzida</span>
                          <span class="metrica-valor">{{ setor.produzida || 0 }}</span>
                        </div>
  
                        <div class="metrica-clean pendente">
                          <span class="metrica-label">Pendente</span>
                          <span class="metrica-valor">{{ setor.previsao_falta_a_ser_produzida || 0 }}</span>
                        </div>
                      </div>
  
                      <!-- ✅ SEÇÃO DE QUANTIDADE (SE SELECIONADO) -->
                      <div v-if="modalSetores.setoresSelecionados.includes(setor.id)" class="quantidade-clean">
                        <v-text-field :model-value="obterQuantidadeSetor(setor.id)"
                          @update:modelValue="definirQuantidadeSetor(setor.id, $event)" type="number"
                          label="Quantidade a planejar" :max="setor.previsao_falta_a_ser_produzida" :min="1" variant="outlined"
                          density="compact" hide-details="auto" @click.stop :rules="[
                            v => v > 0 || 'Quantidade deve ser maior que 0',
                            v => v <= setor.previsao_falta_a_ser_produzida || `Máximo ${setor.previsao_falta_a_ser_produzida}`
                          ]">
                          <template v-slot:append-inner>
                            <span class="limite-text">/ {{ setor.previsao_falta_a_ser_produzida }}</span>
                          </template>
                        </v-text-field>
  
                        <!-- Botões rápidos -->
                        <div class="botoes-rapidos">
                          <v-btn v-for="valor in [50, 100, 200]" :key="valor"
                            @click.stop="definirQuantidadeSetor(setor.id, Math.min(valor, setor.previsao_falta_a_ser_produzida))"
                            size="small" variant="outlined" :disabled="valor > setor.previsao_falta_a_ser_produzida">
                            {{ valor }}
                          </v-btn>
                          <v-btn @click.stop="definirQuantidadeSetor(setor.id, setor.previsao_falta_a_ser_produzida)" size="small"
                            color="primary" variant="flat">
                            MAX
                          </v-btn>
                        </div>
                      </div>
  
                      <!-- Mensagem de erro -->
                      <div v-if="!podeSelecionar(setor)" class="erro-message">
                        <v-alert type="warning" variant="tonal" density="compact">
                          {{ obterMensagemBloqueio(setor) }}
                        </v-alert>
                      </div>
                    </div>
  
                    <!-- ✅ FOOTER SIMPLIFICADO -->
                    <div v-if="podeSelecionar(setor)" class="setor-footer-clean">
                      <v-btn :variant="modalSetores.setoresSelecionados.includes(setor.id) ? 'flat' : 'outlined'"
                        :color="modalSetores.setoresSelecionados.includes(setor.id) ? 'primary' : 'default'" size="small"
                        block @click.stop="toggleSetor(setor.id, setor)">
                        <v-icon size="16">
                          {{ modalSetores.setoresSelecionados.includes(setor.id) ? 'mdi-check' : 'mdi-plus' }}
                        </v-icon>
                        {{ modalSetores.setoresSelecionados.includes(setor.id) ? 'Selecionado' : 'Selecionar' }}
                      </v-btn>
                    </div>
                  </v-card>
                </div>
              </div>
  
              <!-- ✅ RESUMO DA SELEÇÃO MELHORADO -->
              <v-alert v-if="modalSetores.setoresSelecionados.length > 0" type="info" class="mt-4">
                <div class="d-flex align-center justify-space-between">
                  <div>
                    <strong>✅ Setores selecionados ({{ modalSetores.setoresSelecionados.length }}):</strong>
                    <div class="text-caption mt-1">
                      Total planejado: {{ calcularTotalPlanejado() }} unidades
                    </div>
                  </div>
                  <v-btn size="small" color="primary" variant="outlined" @click="ajustarQuantidadesTodosSetores">
                    <v-icon size="small" class="mr-1">mdi-auto-fix</v-icon>
                    Ajustar Todas
                  </v-btn>
                </div>
  
                <div class="selected-setores mt-3">
                  <v-chip v-for="setorId in modalSetores.setoresSelecionados" :key="setorId" class="mr-1 mb-1" closable
                    @click:close="toggleSetor(setorId, modalSetores.setoresDisponiveis.find(s => s.id === setorId))"
                    color="primary" variant="outlined">
                    {{ obterNomeSetor(setorId) }}: {{ obterQuantidadeSetor(setorId) }} un
                  </v-chip>
                </div>
              </v-alert>
  
              <!-- Aviso se nenhum setor selecionado -->
              <v-alert v-else-if="!modalSetores.carregandoSetores" type="warning" class="mt-4">
                ⚠️ Selecione pelo menos um setor para continuar
              </v-alert>
            </div>
          </v-card-text>
  
          <v-card-actions>
            <v-btn @click="fecharModalSetores" text>
              Cancelar
            </v-btn>
            <v-spacer />
            <v-btn @click="confirmarSelecaoSetores" color="primary"
              :disabled="modalSetores.setoresSelecionados.length === 0">
              Confirmar Seleção ({{ modalSetores.setoresSelecionados.length }} setores)
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
  
      <!-- ✅ NOTIFICAÇÃO CUSTOMIZADA -->
      <div v-if="notificacoes.mostrar" class="notificacao-custom" :class="notificacoes.tipo">
        {{ notificacoes.mensagem }}
        <button @click="notificacoes.mostrar = false">×</button>
      </div>
    </div>
  </template>
  <script>
import draggable from 'vuedraggable';
import OrdemMiniCard from '@/components/dashboard/cards/OrdemMiniCard.vue';
import OrdemPlanejamentoCard from '@/components/dashboard/cards/OrdemPlanejamentoCard.vue';
import { producaoService } from '@/services/producaoService';

export default {
  name: 'PlanejamentoSemanal',
  components: {
    draggable,
    OrdemMiniCard,
    OrdemPlanejamentoCard
  },
  data() {
    return {
      // ✅ DADOS INDEPENDENTES
      carregando: true,
      ordensCarregadas: [],
      ultimaAtualizacao: new Date(),

      // Dados originais do componente
      semanaAtual: new Date(),
      salvandoPlanejamento: false,
      diasSemana: [],
      ordensDisponiveis: [],
      filtroOrdens: '',
      planejamentoOriginal: null,
      
      // Filtros avançados
      dialogFiltroAvancado: false,
      filtrosAvancados: {
        clientes: [],
        modelos: [],
        setores: [],
        pedidos: []
      },

      // ✅ REMOVER sistema de notificações do Vuetify
      // mostrarNotificacao: false,
      // mensagemNotificacao: '',
      // tipoNotificacao: 'success',

      // ✅ USAR APENAS sistema de notificações próprio
      notificacoes: {
        mostrar: false,
        tipo: 'success',
        mensagem: ''
      },

      // Modal de seleção de setores
      modalSetores: {
        mostrar: false,
        ordem: null,
        dia: null,
        setoresSelecionados: [],
        setoresDisponiveis: [],
        carregandoSetores: false,
        filtroStatus: 'Todos',
        busca: '',
        quantidadesSetor: {}
      }
    }
  },


    computed: {
      formatarPeriodoSemana() {
        const inicio = this.getInicioSemana(this.semanaAtual);
        const fim = this.getFimSemana(this.semanaAtual);
        return `${inicio.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })} - ${fim.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}`;
      },
  
      numeroSemana() {
        return this.getNumeroSemana(this.semanaAtual);
      },
  
      anoAtual() {
        return this.semanaAtual.getFullYear();
      },
  
      podeVoltarSemana() {
        const hoje = new Date();
        const inicioSemanaAtual = this.getInicioSemana(this.semanaAtual);
        return inicioSemanaAtual >= hoje;
      },
  
      ordensDisponiveisFiltradas() {
        let ordens = this.ordensDisponiveis;

        // Aplicar filtro de texto
        if (this.filtroOrdens) {
          const termo = this.filtroOrdens.toLowerCase();
          ordens = ordens.filter(ordem =>
            ordem.id.toString().includes(termo) ||
            ordem.id_pedido.toString().includes(termo) ||
            ordem.id_ordem.toString().includes(termo) ||
            ordem.cliente?.toLowerCase().includes(termo) ||
            ordem.produto?.toLowerCase().includes(termo)
          );
        }

        // Aplicar filtros avançados
        if (this.filtrosAvancados.clientes.length > 0) {
          ordens = ordens.filter(ordem => this.filtrosAvancados.clientes.includes(ordem.cliente));
        }

        if (this.filtrosAvancados.modelos.length > 0) {
          ordens = ordens.filter(ordem => this.filtrosAvancados.modelos.includes(ordem.produto));
        }

        if (this.filtrosAvancados.setores.length > 0) {
          ordens = ordens.filter(ordem => 
            ordem.proximoSetor && this.filtrosAvancados.setores.includes(ordem.proximoSetor.nome)
          );
        }

        if (this.filtrosAvancados.pedidos.length > 0) {
          ordens = ordens.filter(ordem => this.filtrosAvancados.pedidos.includes(ordem.id_pedido.toString()));
        }

        return ordens;
      },

      // Opções para os selects de filtro avançado
      opcoesClientes() {
        const clientes = [...new Set(this.ordensDisponiveis.map(ordem => ordem.cliente))].sort();
        return clientes.map(cliente => ({ text: cliente, value: cliente }));
      },

      opcoesModelos() {
        const modelos = [...new Set(this.ordensDisponiveis.map(ordem => ordem.produto))].sort();
        return modelos.map(modelo => ({ text: modelo, value: modelo }));
      },

      opcoesSetores() {
        const setores = [...new Set(this.ordensDisponiveis
          .filter(ordem => ordem.proximoSetor)
          .map(ordem => ordem.proximoSetor.nome))].sort();
        return setores.map(setor => ({ text: setor, value: setor }));
      },

      opcoesPedidos() {
        const pedidos = [...new Set(this.ordensDisponiveis.map(ordem => ordem.id_pedido.toString()))].sort();
        return pedidos.map(pedido => ({ text: `Pedido ${pedido}`, value: pedido }));
      },

      // Estados dos checkboxes "Selecionar Todos"
      todosSelecionados() {
        return {
          clientes: this.filtrosAvancados.clientes.length === this.opcoesClientes.length,
          modelos: this.filtrosAvancados.modelos.length === this.opcoesModelos.length,
          setores: this.filtrosAvancados.setores.length === this.opcoesSetores.length,
          pedidos: this.filtrosAvancados.pedidos.length === this.opcoesPedidos.length
        };
      },

      temFiltrosAtivos() {
        return this.filtrosAvancados.clientes.length > 0 ||
               this.filtrosAvancados.modelos.length > 0 ||
               this.filtrosAvancados.setores.length > 0 ||
               this.filtrosAvancados.pedidos.length > 0;
      },

      ordensFiltradasCount() {
        return this.ordensDisponiveisFiltradas.length;
      },
  
      totalOrdensAlocadas() {
        return this.diasSemana.reduce((total, dia) => total + dia.ordens.length, 0);
      },
  
      capacidadeUtilizada() {
        const capacidadeTotal = this.diasSemana.length * 100;
        const capacidadeUsada = this.diasSemana.reduce((total, dia) =>
          total + this.calcularCapacidadeDia(dia), 0
        );
        return Math.round((capacidadeUsada / capacidadeTotal) * 100);
      },
  
      ordensUrgentes() {
        return this.diasSemana.reduce((total, dia) => {
          return total + dia.ordens.filter(ordem => ordem.urgente).length;
        }, 0);
      },
  
      conflitosDetectados() {
        return this.alertasPlanejamento.filter(a => a.tipo === 'conflito').length;
      },
  
      alertasPlanejamento() {
        const alertas = [];
  
        // Verificar capacidade excedida
        this.diasSemana.forEach(dia => {
          const capacidade = this.calcularCapacidadeDia(dia);
          if (capacidade > 100) {
            alertas.push({
              tipo: 'capacidade',
              cor: 'error',
              icone: 'mdi-alert-circle',
              titulo: `${dia.nome} - Capacidade Excedida`,
              descricao: `${capacidade}% de capacidade alocada (máximo recomendado: 100%)`
            });
          }
        });
  
        // Verificar ordens urgentes não alocadas
        const urgentesNaoAlocadas = this.ordensDisponiveis.filter(o => o.urgente);
        if (urgentesNaoAlocadas.length > 0) {
          alertas.push({
            tipo: 'urgente',
            cor: 'warning',
            icone: 'mdi-fire',
            titulo: 'Ordens Urgentes Não Alocadas',
            descricao: `${urgentesNaoAlocadas.length} ordem(ns) urgente(s) ainda não foram alocadas`
          });
        }
  
        return alertas;
      },
  
      temAlteracoes() {
        if (!this.planejamentoOriginal) return false;
  
        const planejamentoAtual = this.gerarPlanejamentoAtual();
        return JSON.stringify(this.planejamentoOriginal) !== JSON.stringify(planejamentoAtual);
      },
  
      setoresFiltrados() {
        if (!this.modalSetores.setoresDisponiveis) return [];
  
        return this.modalSetores.setoresDisponiveis.filter(setor => {
          let passaFiltroStatus = true;
          if (this.modalSetores.filtroStatus === 'Pendentes') {
            passaFiltroStatus = setor.status_geral === 'pendente';
          } else if (this.modalSetores.filtroStatus === 'Finalizados') {
            passaFiltroStatus = setor.status_geral === 'finalizado';
          } else if (this.modalSetores.filtroStatus === 'Sem produção') {
            passaFiltroStatus = setor.status_geral === 'sem';
          }
  
          let passaBusca = true;
          if (this.modalSetores.busca) {
            passaBusca = setor.nome.toLowerCase().includes(this.modalSetores.busca.toLowerCase());
          }
  
          return passaFiltroStatus && passaBusca;
        });
      },
  
      setoresFiltradosDisponiveis() {
        return this.setoresFiltrados.filter(setor => this.podeSelecionar(setor));
      },
  
      todosSetoresSelecionados() {
        if (this.setoresFiltradosDisponiveis.length === 0) return false;
  
        return this.setoresFiltradosDisponiveis.every(setor =>
          this.modalSetores.setoresSelecionados.includes(setor.id)
        );
      }
    },
  
    watch: {
      semanaAtual() {
        this.carregarPlanejamentoSemana();
      }
    },
  
    async mounted() {
      await this.inicializarDados();
    },
  
    beforeUnmount() {
      if (this.intervalId) {
        clearInterval(this.intervalId);
      }
    },
  
    methods: {
      // ✅ MÉTODOS INDEPENDENTES COPIADOS DO PAI
      async inicializarDados() {
        this.carregando = true;
        try {
          console.log('🚀 Inicializando dados do planejamento...');
  
          await this.carregarOrdensDisponiveis();
          this.gerarDiasSemana();
          await this.carregarPlanejamentoSemana();
        } catch (error) {
          console.error('Erro ao inicializar dados:', error);
          this.mostrarNotificacaoErro('Erro ao carregar dados iniciais');
        } finally {
          this.carregando = false;
        }
      },
  
      async carregarOrdensDisponiveis() {
        try {
  
          const chaveSemana = this.getChaveSemana();
          const response = await producaoService.buscarOrdensDisponiveis(chaveSemana);
  
          if (response.status === 'sucesso') {
            this.ordensDisponiveis = response.ordens.map(ordem => ({
              id: ordem.id,
              id_ordem: ordem.id_ordem,
              id_pedido: ordem.id_pedido,
              cliente: ordem.cliente,
              produto: ordem.produto,
              quantidadeTotal: ordem.quantidade_total,
              prioridade: ordem.prioridade,
              prazoEntrega: ordem.prazo_entrega,
              prazoEntregaFormatado: ordem.prazo_entrega_formatado,
              urgente: ordem.urgente,
              atrasada: ordem.atrasada || false,
              statusDashboard: ordem.status_dashboard,
              proximoSetor: ordem.proximo_setor,
              tempoEstimado: ordem.tempo_estimado,
              setoresDisponiveis: ordem.setores_disponiveis || []
            }));

            // Salvar também em ordensCarregadas para o filtro funcionar
            this.ordensCarregadas = [...this.ordensDisponiveis];
          } else {
            throw new Error(response.message || 'Erro ao carregar ordens');
          }
        } catch (error) {
          console.error('Erro ao carregar ordens:', error);
          this.mostrarNotificacaoErro('Erro ao carregar ordens disponíveis');
          this.ordensDisponiveis = [];
        }
      },
  
      // Sistema de notificações
      mostrarNotificacaoSucesso(mensagem) {
        this.notificacoes = { mostrar: true, tipo: 'success', mensagem };
        setTimeout(() => this.notificacoes.mostrar = false, 4000);
      },

      mostrarNotificacaoErro(mensagem) {
        this.notificacoes = { mostrar: true, tipo: 'error', mensagem };
        setTimeout(() => this.notificacoes.mostrar = false, 4000);
      },

      mostrarNotificacaoInfo(mensagem) {
        this.notificacoes = { mostrar: true, tipo: 'info', mensagem };
        setTimeout(() => this.notificacoes.mostrar = false, 4000);
      },

      // Métodos para filtros avançados
      abrirFiltroAvancado() {
        this.dialogFiltroAvancado = true;
      },

      fecharFiltroAvancado() {
        this.dialogFiltroAvancado = false;
      },

      aplicarFiltros() {
        this.dialogFiltroAvancado = false;
        this.mostrarNotificacaoSucesso(`Filtros aplicados! ${this.ordensFiltradasCount} ordens encontradas.`);
      },

      limparFiltros() {
        this.filtrosAvancados = {
          clientes: [],
          modelos: [],
          setores: [],
          pedidos: []
        };
        this.mostrarNotificacaoSucesso('Filtros removidos!');
      },

      selecionarTodosClientes() {
        if (this.todosSelecionados.clientes) {
          this.filtrosAvancados.clientes = [];
        } else {
          this.filtrosAvancados.clientes = this.opcoesClientes.map(opcao => opcao.value);
        }
      },

      selecionarTodosModelos() {
        if (this.todosSelecionados.modelos) {
          this.filtrosAvancados.modelos = [];
        } else {
          this.filtrosAvancados.modelos = this.opcoesModelos.map(opcao => opcao.value);
        }
      },

      selecionarTodosPedidos() {
        if (this.todosSelecionados.pedidos) {
          this.filtrosAvancados.pedidos = [];
        } else {
          this.filtrosAvancados.pedidos = this.opcoesPedidos.map(opcao => opcao.value);
        }
      },

      // Métodos para remover itens individuais dos filtros
      removerCliente(cliente) {
        this.filtrosAvancados.clientes = this.filtrosAvancados.clientes.filter(c => c !== cliente);
      },

      removerModelo(modelo) {
        this.filtrosAvancados.modelos = this.filtrosAvancados.modelos.filter(m => m !== modelo);
      },

      removerSetor(setor) {
        this.filtrosAvancados.setores = this.filtrosAvancados.setores.filter(s => s !== setor);
      },

      removerPedido(pedido) {
        this.filtrosAvancados.pedidos = this.filtrosAvancados.pedidos.filter(p => p !== pedido);
      },

      // Método para filtrar opções nos selects
      filtrarOpcoes(item, queryText, itemText) {
        if (!queryText) return true;
        const texto = itemText.toLowerCase();
        const busca = queryText.toLowerCase();
        return texto.includes(busca);
      },
  
      formatarHorario(data) {
        try {
          return new Date(data).toLocaleString('pt-BR');
        } catch (error) {
          return 'Horário inválido';
        }
      },
  
        // ✅ CORRIGIR MÉTODOS QUE USAVAM $toast
        selecionarTodosSetores() {
      const setoresSelecionaveis = this.setoresFiltradosDisponiveis.filter(setor => {
        return this.podeSelecionar(setor);
      });

      this.modalSetores.setoresSelecionados = [];

      setoresSelecionaveis.forEach(setor => {
        this.modalSetores.setoresSelecionados.push(setor.id);
        const quantidadeBrunoPrevisao = setor.previsao_falta_a_ser_produzida;
        this.definirQuantidadeSetor(setor.id, quantidadeBrunoPrevisao);
      });

      // ✅ CORRIGIDO
      this.mostrarNotificacaoSucesso(`✅ ${setoresSelecionaveis.length} setores selecionados`);
    },

    ajustarQuantidadesTodosSetores() {
      const quantidadeTotal = this.modalSetores.ordem?.previsao_falta_a_ser_produzida || 0;
      const setoresSelecionados = this.modalSetores.setoresSelecionados.length;

      if (setoresSelecionados === 0) return;

      const quantidadePorSetor = Math.floor(quantidadeTotal / setoresSelecionados);

      this.modalSetores.setoresSelecionados.forEach(setorId => {
        const setor = this.modalSetores.setoresDisponiveis.find(s => s.id === setorId);
        if (setor) {
          const quantidade = Math.min(quantidadePorSetor, setor.previsao_falta_a_ser_produzida);
          this.definirQuantidadeSetor(setorId, quantidade);
        }
      });

      // ✅ CORRIGIDO
      this.mostrarNotificacaoSucesso('✅ Quantidades ajustadas automaticamente');
    },

    toggleSetor(setorId, setor) {
      if (!this.podeSelecionar(setor)) {
        return;
      }

      const index = this.modalSetores.setoresSelecionados.indexOf(setorId);

      if (index === -1) {
        this.modalSetores.setoresSelecionados.push(setorId);

        const quantidadeTotal = this.modalSetores.ordem?.quantidadeTotal || 0;
        const quantidadePadrao = Math.min(
          Math.max(100, Math.floor(quantidadeTotal / 3)),
          setor.previsao_falta_a_ser_produzida
        );

        this.definirQuantidadeSetor(setorId, quantidadePadrao);
        // ✅ CORRIGIDO
        this.mostrarNotificacaoSucesso(`✅ ${setor.nome} selecionado (${quantidadePadrao} un)`);
      } else {
        this.modalSetores.setoresSelecionados.splice(index, 1);
        this.definirQuantidadeSetor(setorId, 0);
        // ✅ CORRIGIDO
        this.mostrarNotificacaoInfo(`❌ ${setor.nome} removido`);
      }
    },

  
      calcularTotalPlanejado() {
        return this.modalSetores.setoresSelecionados.reduce((total, setorId) => {
          return total + this.obterQuantidadeSetor(setorId);
        }, 0);
      },
  
      obterMensagemBloqueio(setor) {
        if (['FINALIZADO', 'FINALIZADO ERRO'].includes(setor.nome)) {
          return 'Setor já finalizado';
        }
  
        if (!setor.previsao_falta_a_ser_produzida || setor.previsao_falta_a_ser_produzida <= 0) {
          return 'Sem saldo disponível';
        }
  
        const statusLower = setor.status_geral?.toLowerCase() || '';
        if (statusLower.includes('finalizado')) {
          return 'Processo finalizado';
        }
  
        return 'Não disponível';
      },
  
      async onOrdemAdicionada(evt, dia) {
        const ordemIndex = evt.newIndex;
        const ordem = dia.ordens[ordemIndex];
  
        if (!ordem) {
          console.error('❌ Ordem não encontrada');
          return;
        }
  
        // Verificar se já existe uma ordem sendo processada no modal
        if (this.modalSetores.mostrar) {
          console.log('⚠️ Modal já está aberto, aguardando conclusão...');
          // Remove a ordem temporariamente e aguarda o modal atual ser fechado
          dia.ordens.splice(ordemIndex, 1);
          return;
        }
  
        dia.ordens.splice(ordemIndex, 1);
        this.abrirModalSetores(ordem, dia);
      },
  
      async abrirModalSetores(ordem, dia) {
        this.modalSetores = {
          mostrar: true,
          ordem: { ...ordem },
          dia: { ...dia },
          setoresSelecionados: [],
          setoresDisponiveis: [],
          carregandoSetores: true
        };
  
        try {
          await this.carregarSetoresDaOrdem(ordem.id);
        } catch (error) {
          console.error('❌ Erro ao carregar setores:', error);
          this.mostrarNotificacaoErro('Erro ao carregar setores da ordem');
          this.modalSetores.mostrar = false;
        }
      },
  
      async carregarSetoresDaOrdem(ordemId) {
        try {
          const response = await producaoService.buscarSaldosPorOrdem(ordemId);
  
          if (response.status === 'sucesso' && response.data && response.data.saldos) {
            const setoresUnicos = new Map();
  
            response.data.saldos.forEach((saldo) => {
              if (!setoresUnicos.has(saldo.id_setor)) {
                setoresUnicos.set(saldo.id_setor, {
                  id: saldo.id_setor,
                  nome: saldo.nome_setor,
                  funcionario_atual: saldo.funcionario_atual_nome,
                  funcionario_atual_id: saldo.funcionario_atual_id,
                  ordem_exibicao: saldo.ordem_exibicao_tela,
                  status_geral: saldo.status_geral,
                  planejada_inicial: saldo.planejada_inicial,
                  planejada_final: saldo.planejada_final,
                  saldo_disponivel: saldo.saldo_disponivel_produzir,
                  saldo_disponivel_produzir: saldo.saldo_disponivel_produzir,
                  produzida: saldo.produzida,
                  recebida_aguardando_aprovocao: saldo.recebida_aguardando_aprovocao,
                  recebida_aprovada: saldo.recebida_aprovada,
                  recebida_qts: saldo.recebida_qts,
                  saidas: saldo.saidas,
                  transito: saldo.transito,
                  parte: saldo.parte,
                  tamanho: saldo.tamanho,
                  id_ordem_producao_status_cadastro: saldo.id_ordem_producao_status_cadastro,
                  previsao_falta_a_ser_produzida: saldo.previsao_falta_a_ser_produzida,
                  status_aprovacao_enviadas: saldo.status_aprovacao_enviadas,
                  status_aprovacao_recebimento: saldo.status_aprovacao_recebimento,
                  movimentacoes: saldo.movimentacoes,
                  tempo_estimado: this.calcularTempoEstimadoSetor(saldo),
                  capacidade_utilizada: this.calcularCapacidadeSetor(saldo)
                });
              }
            });
  
            this.modalSetores.setoresDisponiveis = Array.from(setoresUnicos.values())
              .sort((a, b) => a.ordem_exibicao - b.ordem_exibicao);
  
            console.log('✅ Setores únicos processados:', this.modalSetores.setoresDisponiveis);
          } else {
            console.error('❌ Resposta inválida da API:', response);
            throw new Error('Dados de setores não encontrados');
          }
        } catch (error) {
          console.error('❌ Erro ao buscar setores:', error);
          throw error;
        } finally {
          this.modalSetores.carregandoSetores = false;
        }
      },
  
      async testarBuscarSetores() {
        try {
          console.log('🧪 Testando busca de setores...');
  
          const primeiraOrdem = this.ordensDisponiveis[0];
          if (!primeiraOrdem) {
            console.error('❌ Nenhuma ordem disponível para teste');
            return;
          }
  
          console.log('📋 Testando com ordem:', primeiraOrdem);
          const response = await producaoService.buscarSaldosPorOrdem(88);
          console.log('📡 Resposta do teste:', response);
        } catch (error) {
          console.error('❌ Erro no teste:', error);
        }
      },
  
      calcularTempoEstimadoSetor(saldo) {
        const quantidade = saldo.planejada_final || 0;
        const tempoBase = this.getTempoBasePorSetor(saldo.nome_setor);
        const tempoTotal = (quantidade * tempoBase) / 60;
  
        return tempoTotal > 1
          ? `${Math.round(tempoTotal)}h`
          : `${Math.round(tempoTotal * 60)}min`;
      },
  
      calcularCapacidadeSetor(saldo) {
        const produzida = saldo.produzida || 0;
        const planejada = saldo.planejada_final || 1;
        const percentual = Math.round((produzida / planejada) * 100);
  
        return `${percentual}%`;
      },
  
      getTempoBasePorSetor(nomeSetor) {
        const temposBase = {
          'PCP': 2,
          'ESTOQUE': 1,
          'RISCO': 5,
          'CORTE': 8,
          'SEPARAÇÃO': 3,
          'BORDADO': 15,
          'BORDADO PELO': 10,
          'PREPARAÇÃO': 12,
          'COSTURA - FARDAMENTO MALHA': 25,
          'ACABAMENTO': 8,
          'EMBALAGEM 2': 5,
          'RECUPERACAO': 20,
          'FINALIZADO ERRO': 0,
          'FATURAMENTO': 2,
          'FINALIZADO': 0
        };
  
        return temposBase[nomeSetor] || 10;
      },
  
      obterQuantidadeSetor(setorId) {
        if (!this.modalSetores.quantidadesSetor) {
        this.modalSetores.quantidadesSetor = {};
      }
      return this.modalSetores.quantidadesSetor[setorId] || 0;
    },

    definirQuantidadeSetor(setorId, quantidade) {
      if (!this.modalSetores.quantidadesSetor) {
        this.modalSetores.quantidadesSetor = {};
      }

      const qtd = parseInt(quantidade) || 0;
      const setor = this.modalSetores.setoresDisponiveis.find(s => s.id === setorId);

      if (setor) {
        const valorFinal = Math.min(Math.max(0, qtd), setor.previsao_falta_a_ser_produzida);
        this.modalSetores.quantidadesSetor[setorId] = valorFinal;
        console.log(`Setor ${setorId}: quantidade definida para ${valorFinal}`);
      } else {
        console.warn(`Setor ${setorId} não encontrado`);
      }
    },

    podeSelecionar(setor) {
      if (!setor) return false;

      if (['FINALIZADO', 'FINALIZADO ERRO'].includes(setor.nome)) {
        return false;
      }

      if (!setor.previsao_falta_a_ser_produzida || setor.previsao_falta_a_ser_produzida <= 0) {
        return false;
      }

      const statusLower = setor.status_geral?.toLowerCase() || '';
      if (statusLower.includes('finalizado')) {
        return false;
      }

      return true;
    },

    obterNomeSetor(setorId) {
      const setor = this.modalSetores.setoresDisponiveis.find(s => s.id === setorId);
      return setor ? setor.nome : `Setor ${setorId}`;
    },

    toggleSetor(setorId, setor) {
      if (!this.podeSelecionar(setor)) {
        return;
      }

      const index = this.modalSetores.setoresSelecionados.indexOf(setorId);

      if (index === -1) {
        this.modalSetores.setoresSelecionados.push(setorId);

        const quantidadeTotal = this.modalSetores.ordem?.quantidadeTotal || 0;
        const quantidadePadrao = Math.min(
          Math.max(100, Math.floor(quantidadeTotal / 3)),
          setor.previsao_falta_a_ser_produzida
        );

        this.definirQuantidadeSetor(setorId, quantidadePadrao);
        this.mostrarNotificacaoSucesso(`✅ ${setor.nome} selecionado (${quantidadePadrao} un)`);
      } else {
        this.modalSetores.setoresSelecionados.splice(index, 1);
        this.definirQuantidadeSetor(setorId, 0);
        this.mostrarNotificacaoInfo(`❌ ${setor.nome} removido`);
      }
    },

    async confirmarSelecaoSetores() {
      if (this.modalSetores.setoresSelecionados.length === 0) {
        // ✅ CORRIGIDO
        this.mostrarNotificacaoErro('Selecione pelo menos um setor!');
        return;
      }

      try {
        const { ordem, dia, setoresSelecionados } = this.modalSetores;

        const ordemComSetores = {
          ...ordem,
          setores_agendados: setoresSelecionados.map(setorId => ({
            setor_id: setorId,
            setor_nome: this.getSetorNome(setorId),
            dia_agendado: dia.data,
            dia_nome: dia.nome
          })),
          dia_agendado: dia.data,
          status_planejamento: 'agendada'
        };

        const diaIndex = this.diasSemana.findIndex(d => d.data === dia.data);
        if (diaIndex !== -1) {
          this.diasSemana[diaIndex].ordens.push(ordemComSetores);
        }

        this.removerOrdemDisponivel(ordem.id);
        await this.salvarAgendamento(ordemComSetores);

        this.modalSetores.mostrar = false;

        // ✅ CORRIGIDO
        this.mostrarNotificacaoSucesso(`✅ Ordem #${ordem.id_ordem} agendada para ${dia.nome} com ${setoresSelecionados.length} setores!`);

        console.log('✅ Ordem agendada com sucesso:', ordemComSetores);

      } catch (error) {
        console.error('❌ Erro ao confirmar agendamento:', error);
        // ✅ CORRIGIDO
        this.mostrarNotificacaoErro('Erro ao agendar ordem');
      }
    },


    cancelarSelecaoSetores() {
      console.log('❌ Cancelando seleção de setores');

      try {
        this.modalSetores.mostrar = false;
        this.modalSetores.setoresSelecionados = [];

        if (this.modalSetores.ordem && this.modalSetores.dia) {
          const diaIndex = this.diasSemana.findIndex(d =>
            d.data.toDateString() === this.modalSetores.dia.data.toDateString());

          if (diaIndex !== -1) {
            const ordemIndex = this.diasSemana[diaIndex].ordens.findIndex(o =>
              o.id === this.modalSetores.ordem.id);

            if (ordemIndex !== -1) {
              const ordemRemovida = this.diasSemana[diaIndex].ordens.splice(ordemIndex, 1)[0];
              console.log('❌ Ordem removida do dia após cancelamento:', ordemRemovida);

              if (!this.ordensDisponiveis.find(o => o.id === ordemRemovida.id)) {
                this.ordensDisponiveis.push(ordemRemovida);
              }
            }
          }
        }

        console.log('✅ Seleção de setores cancelada com sucesso');
      } catch (error) {
        console.error('❌ Erro ao cancelar seleção de setores:', error);
      }
    },

    fecharModalSetores() {
      console.log('❌ Fechando modal de setores');
      this.cancelarSelecaoSetores();
    },

    getSetorNome(setorId) {
      const setor = this.modalSetores.setoresDisponiveis.find(s => s.id === setorId);
      return setor ? setor.nome : `Setor ${setorId}`;
    },

    removerOrdemDisponivel(ordemId) {
      const index = this.ordensDisponiveisFiltradas.findIndex(o => o.id === ordemId);
      if (index > -1) {
        this.ordensDisponiveisFiltradas.splice(index, 1);
      }
    },

    async salvarAgendamento(ordemComSetores) {
      try {
        console.log('💾 Salvando agendamento:', ordemComSetores);

        const dadosAgendamento = {
          ordem_id: ordemComSetores.id,
          dia_agendado: ordemComSetores.dia_agendado,
          setores: ordemComSetores.setores_agendados.map(s => ({
            setor_id: s.setor_id,
            setor_nome: s.setor_nome,
            quantidade: s.quantidade || 1,
            id_cadastro_status: s.id_cadastro_status || 1
          })),
          usuario: 'usuario_atual',
          observacoes: `Agendado via dashboard para ${ordemComSetores.setores_agendados.length} setores`
        };

        const response = await producaoService.salvarPlanejamentoSemanal(dadosAgendamento);

        if (response.status !== 'sucesso') {
          throw new Error(response.message || 'Erro ao salvar agendamento');
        }

        console.log('✅ Agendamento salvo no backend');

      } catch (error) {
        console.error('❌ Erro ao salvar agendamento:', error);
        throw error;
      }
    },

    // ✅ MÉTODOS ORIGINAIS DO COMPONENTE
    gerarDiasSemana() {
      const inicio = this.getInicioSemana(this.semanaAtual);
      this.diasSemana = [];

      for (let i = 0; i < 7; i++) {
        const data = new Date(inicio);
        data.setDate(inicio.getDate() + i);

        this.diasSemana.push({
          nome: data.toLocaleDateString('pt-BR', { weekday: 'short' }).toUpperCase(),
          data: new Date(data),
          ordens: []
        });
      }
    },

    async carregarPlanejamentoSemana() {
      try {
        const chaveSemana = this.getChaveSemana();
        const response = await producaoService.buscarPlanejamentoSemana(chaveSemana);

        if (response.status === 'sucesso' && response.planejamento) {
          this.aplicarPlanejamento(response.planejamento);
        } else {
          this.diasSemana.forEach(dia => {
            dia.ordens = [];
          });
        }

        this.salvarPlanejamentoOriginal();
        this.atualizarOrdensDisponiveis();

      } catch (error) {
        console.error('Erro ao carregar planejamento:', error);
        this.mostrarNotificacaoErro('Erro ao carregar planejamento da semana');
      }
    },

    aplicarPlanejamento(planejamento) {
      if (!planejamento.dias) return;

      planejamento.dias.forEach((diaData, index) => {
        if (this.diasSemana[index] && diaData.ordens) {
          this.diasSemana[index].ordens = diaData.ordens.map(ordemRef => {
            const ordem = this.ordensCarregadas.find(o => o.id === ordemRef.id);
            if (ordem) {
              return { ...ordem, posicaoNoDia: ordemRef.posicao };
            }
            return null;
          }).filter(Boolean);
        }
      });
    },

    async salvarPlanejamento() {
      if (!this.temAlteracoes) {
        this.mostrarNotificacaoInfo('Nenhuma alteração para salvar');
        return;
      }

      this.salvandoPlanejamento = true;
      try {
        const chaveSemana = this.getChaveSemana();
        const planejamento = {
          semana: chaveSemana,
          dias: this.diasSemana.map(dia => ({
            data: dia.data.toISOString(),
            ordens: dia.ordens.map((ordem, index) => ({
              id: ordem.id,
              posicao: index
            }))
          }))
        };

        const response = await producaoService.salvarPlanejamentoSemana(planejamento);

        if (response.status === 'sucesso') {
          this.diasSemana.forEach(dia => {
            dia.ordens.forEach(ordem => {
              ordem.semanaAlocada = chaveSemana;
              ordem.diaAlocado = dia.data;
            });
          });

          this.salvarPlanejamentoOriginal();
          this.atualizarOrdensDisponiveis();

          this.$emit('salvar-planejamento', planejamento);
          this.mostrarNotificacaoSucesso('Planejamento salvo com sucesso!');
        } else {
          throw new Error(response.message || 'Erro ao salvar planejamento');
        }

      } catch (error) {
        console.error('Erro ao salvar planejamento:', error);
        this.mostrarNotificacaoErro(
          error.message || 'Erro ao salvar planejamento'
        );
      } finally {
        this.salvandoPlanejamento = false;
      }
    },

    gerarPlanejamentoAtual() {
      return {
        semana: this.getChaveSemana(),
        dias: this.diasSemana.map(dia => ({
          data: dia.data.toISOString(),
          ordens: dia.ordens.map((ordem, index) => ({
            id: ordem.id,
            posicao: index
          }))
        }))
      };
    },

    salvarPlanejamentoOriginal() {
      this.planejamentoOriginal = JSON.parse(JSON.stringify(this.gerarPlanejamentoAtual()));
    },

    atualizarOrdensDisponiveis() {
      const chaveSemana = this.getChaveSemana();
      this.ordensDisponiveis = this.ordensCarregadas.filter(ordem =>
        !ordem.semanaAlocada || ordem.semanaAlocada !== chaveSemana
      );
    },

    async semanaAnterior() {
      if (this.temAlteracoes && !confirm('Existem alterações não salvas. Deseja continuar mesmo assim?')) {
        return;
      }

      const novaSemana = new Date(this.semanaAtual);
      novaSemana.setDate(novaSemana.getDate() - 7);
      this.semanaAtual = novaSemana;

      this.carregando = true;
      try {
        console.log('📅 Mudando para semana anterior:', this.getChaveSemana());

        await this.carregarOrdensDisponiveis();
        this.gerarDiasSemana();
        await this.carregarPlanejamentoSemana();

      } catch (error) {
        console.error('❌ Erro ao mudar para semana anterior:', error);
        this.mostrarNotificacaoErro('Erro ao carregar dados da semana anterior');
      } finally {
        this.carregando = false;
      }
    },

    async proximaSemana() {
      if (this.temAlteracoes && !confirm('Existem alterações não salvas. Deseja continuar mesmo assim?')) {
        return;
      }

      const novaSemana = new Date(this.semanaAtual);
      novaSemana.setDate(novaSemana.getDate() + 7);
      this.semanaAtual = novaSemana;

      this.carregando = true;
      try {
        console.log('📅 Mudando para próxima semana:', this.getChaveSemana());

        await this.carregarOrdensDisponiveis();
        this.gerarDiasSemana();
        await this.carregarPlanejamentoSemana();

      } catch (error) {
        console.error('❌ Erro ao mudar para próxima semana:', error);
        this.mostrarNotificacaoErro('Erro ao carregar dados da próxima semana');
      } finally {
        this.carregando = false;
      }
    },

    onOrdemRemovida(event, dia) {
      this.onPlanejamentoAlterado();
    },

    onPlanejamentoAlterado() {
      // Método chamado sempre que o planejamento é alterado
    },

    removerOrdemDoDia(ordem, dia) {
      const index = dia.ordens.findIndex(o => o.id === ordem.id);
      if (index !== -1) {
        dia.ordens.splice(index, 1);

        delete ordem.diaAlocado;
        delete ordem.semanaAlocada;

        if (!this.ordensDisponiveis.find(o => o.id === ordem.id)) {
          this.ordensDisponiveis.push(ordem);
        }

        this.onPlanejamentoAlterado();
      }
    },

    clonarOrdem(ordem) {
      return { ...ordem };
    },

    calcularCapacidadeDia(dia) {
      let capacidade = 0;

      dia.ordens.forEach(ordem => {
        let capacidadeOrdem = 20;

        if (ordem.quantidadeTotal > 1000) capacidadeOrdem += 10;
        if (ordem.quantidadeTotal > 2000) capacidadeOrdem += 10;

        if (ordem.urgente) capacidadeOrdem += 5;
        if (ordem.atrasada) capacidadeOrdem += 10;

        capacidade += capacidadeOrdem;
      });

      return Math.round(capacidade);
    },

    getCorCapacidade(capacidade) {
      if (capacidade > 100) return 'error';
      if (capacidade > 80) return 'warning';
      if (capacidade > 60) return 'info';
      return 'success';
    },

    getCorStatusSetor(status) {
      if (!status) return 'grey';

      switch (status.toLowerCase()) {
        case 'finalizado':
          return 'success';
        case 'pendente':
          return 'warning';
        case 'em_andamento':
          return 'info';
        case 'sem':
          return 'grey';
        case 'atrasado':
          return 'error';
        default:
          return 'grey';
      }
    },

    getInicioSemana(data) {
      const inicio = new Date(data);
      const dia = inicio.getDay();
      const diff = inicio.getDate() - dia + (dia === 0 ? -6 : 1);
      inicio.setDate(diff);
      inicio.setHours(0, 0, 0, 0);
      return inicio;
    },

    getFimSemana(data) {
      const inicio = this.getInicioSemana(data);
      const fim = new Date(inicio);
      fim.setDate(inicio.getDate() + 6);
      fim.setHours(23, 59, 59, 999);
      return fim;
    },

    getChaveSemana() {
      const inicio = this.getInicioSemana(this.semanaAtual);
      return `${inicio.getFullYear()}-W${this.getNumeroSemana(inicio)}`;
    },

    getNumeroSemana(data) {
      const inicio = new Date(data.getFullYear(), 0, 1);
      const dias = Math.floor((data - inicio) / (24 * 60 * 60 * 1000));
      return Math.ceil((dias + inicio.getDay() + 1) / 7);
    },

    ehHoje(data) {
      const hoje = new Date();
      return data.toDateString() === hoje.toDateString();
    },

    ehFimDeSemana(data) {
      const dia = data.getDay();
      return dia === 0 || dia === 6;
    },

    formatarDia(data) {
      return data.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit'
      });
    }
  }
}
</script>

<style scoped>
/* ✅ ESTILOS ORIGINAIS + NOVOS */
.planejamento-semanal {
  min-height: 600px;
  padding: 20px;
  font-family: 'Segoe UI', Arial, sans-serif;
  background: #f5f7fa;
}

.bg-gradient-success {
  background: linear-gradient(135deg, #4caf50 0%, #388e3c 100%);
}

.ordens-disponiveis-container {
  height: 600px;
  overflow-y: auto;
}

.ordens-disponiveis {
  min-height: 100px;
}

.semana-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  height: 600px;
  border: 1px solid #e0e0e0;
}

.dia-coluna {
  border-right: 1px solid #e0e0e0;
  display: flex;
  flex-direction: column;
  background: white;
}

.dia-coluna:last-child {
  border-right: none;
}

.dia-hoje {
  background: rgba(25, 118, 210, 0.05);
}

.dia-fim-semana {
  background: rgba(158, 158, 158, 0.05);
}

.dia-header {
  padding: 12px 8px;
  background: #f5f5f5;
  border-bottom: 1px solid #e0e0e0;
  text-align: center;
  min-height: 80px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 4px;
}

.dia-conteudo {
  flex: 1;
  padding: 8px;
  overflow-y: auto;
}

.dia-footer {
  padding: 8px;
  border-top: 1px solid #e0e0e0;
  background: #fafafa;
}

.drop-zone {
  min-height: 100%;
  border-radius: 4px;
  transition: all 0.3s ease;
}

.drop-zone-empty {
  border: 2px dashed #e0e0e0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.drop-zone-over-capacity {
  background: rgba(244, 67, 54, 0.05);
  border: 2px dashed #f44336;
}

.drop-placeholder {
  text-align: center;
  padding: 20px;
  color: #9e9e9e;
}

/* ✅ LOADING */
.loading-container {
  text-align: center;
  padding: 80px 20px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* ✅ NOTIFICAÇÕES CUSTOMIZADAS */
.notificacao-custom {
  position: fixed;
  top: 20px;
  right: 20px;
  padding: 12px 20px;
  border-radius: 8px;
  color: white;
  font-weight: 500;
  z-index: 9999;
  display: flex;
  align-items: center;
  gap: 10px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.notificacao-custom.success {
  background: #27ae60;
}

.notificacao-custom.error {
  background: #e74c3c;
}

.notificacao-custom.info {
  background: #3498db;
}

.notificacao-custom button {
  background: none;
  border: none;
  color: white;
  font-size: 18px;
  cursor: pointer;
  padding: 0;
  width: 20px;
  height: 20px;
}

/* ✅ MODAL DE SETORES */
.ordem-info {
  background: #f5f5f5;
  padding: 15px;
  border-radius: 8px;
  margin-bottom: 20px;
}

.ordem-info h3 {
  margin: 0 0 10px 0;
  color: #1976d2;
}

.ordem-info p {
  margin: 5px 0;
  color: #333;
}

.setores-selection h4 {
  margin-bottom: 20px;
  color: #333;
}

/* ✅ CONTAINER PRINCIPAL */
.setores-grid-clean {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 24px;
  padding: 8px;
}

/* ✅ CARD BASE */
.setor-card-clean {
  border: 2px solid #e2e8f0;
  border-radius: 16px;
  overflow: hidden;
  transition: all 0.3s ease;
  background: white;
  cursor: pointer;
}

.setor-card-clean:hover:not(.setor-disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  border-color: #3b82f6;
}

.setor-card-clean.selected {
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.setor-card-clean.setor-disabled {
  opacity: 0.6;
  cursor: not-allowed;
  background: #f8fafc;
}

.setor-card-clean.setor-sem-saldo {
  border-color: #f59e0b;
  background: #fffbeb;
}

/* ✅ HEADER LIMPO */
.setor-header-clean {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
}

.setor-card-clean.selected .setor-header-clean {
  background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
}

.header-main {
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;
}

.setor-info {
  flex: 1;
}

.setor-titulo {
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 8px 0;
  line-height: 1.2;
}

.setor-tags {
  display: flex;
  align-items: center;
  gap: 12px;
}

.setor-detalhes {
  font-size: 13px;
  color: #64748b;
  font-weight: 500;
}

/* ✅ STATUS BADGE */
.status-badge {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.status-badge.disponivel {
  background: #dcfce7;
  color: #166534;
}

.status-badge.indisponivel {
  background: #fee2e2;
  color: #991b1b;
}

/* ✅ CORPO DO CARD */
.setor-body-clean {
  padding: 20px;
}

/* ✅ INFORMAÇÕES */
.info-row-clean {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 20px;
}

.info-item-clean {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: #f1f5f9;
  border-radius: 8px;
}

.info-text {
  font-size: 13px;
  font-weight: 600;
  color: #334155;
}

/* ✅ MÉTRICAS SIMPLIFICADAS (SÓ 3) */
.metricas-clean {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-bottom: 20px;
}

.metrica-clean {
  text-align: center;
  padding: 16px 12px;
  border-radius: 12px;
  border: 2px solid transparent;
  transition: all 0.2s ease;
}

.metrica-clean:hover {
  transform: translateY(-1px);
}

.metrica-clean.planejada {
  background: #dbeafe;
  border-color: #3b82f6;
}

.metrica-clean.produzida {
  background: #dcfce7;
  border-color: #10b981;
}

.metrica-clean.pendente {
  background: #fef3c7;
  border-color: #f59e0b;
}

.metrica-label {
  display: block;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: #64748b;
  margin-bottom: 4px;
}

.metrica-valor {
  display: block;
  font-size: 20px;
  font-weight: 800;
  color: #1e293b;
}

/* ✅ SEÇÃO DE QUANTIDADE */
.quantidade-clean {
  background: #f0f9ff;
  border-radius: 12px;
  padding: 16px;
  border: 1px solid #bae6fd;
  margin-bottom: 16px;
}

.limite-text {
  font-size: 12px;
  color: #64748b;
  font-weight: 600;
}

.botoes-rapidos {
  display: flex;
  gap: 8px;
  margin-top: 12px;
  justify-content: center;
}

/* ✅ MENSAGEM DE ERRO */
.erro-message {
  margin-bottom: 16px;
}

/* ✅ FOOTER */
.setor-footer-clean {
  padding: 16px 20px;
  background: #f8fafc;
  border-top: 1px solid #e2e8f0;
}

/* Animações de drag and drop */
.sortable-ghost {
  opacity: 0.5;
  background: #f5f5f5;
  transform: rotate(2deg);
}

.sortable-chosen {
  transform: scale(1.02);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
}

.sortable-drag {
  transform: rotate(5deg);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
}

.gap-1>*+* {
  margin-left: 4px;
}

.gap-2>*+* {
  margin-left: 8px;
}

/* Responsividade */
@media (max-width: 1200px) {
  .semana-grid {
    grid-template-columns: repeat(4, 1fr);
    height: auto;
  }

  .dia-coluna {
    min-height: 200px;
  }
}

@media (max-width: 768px) {
  .planejamento-semanal {
    padding: 10px;
  }

  .semana-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .ordens-disponiveis-container {
    height: 400px;
  }

  .setores-grid-clean {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .info-row-clean {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .metricas-clean {
    grid-template-columns: 1fr;
    gap: 8px;
  }

  .setor-header-clean {
    padding: 16px;
  }

  .setor-body-clean {
    padding: 16px;
  }
}

@media (max-width: 600px) {
  .semana-grid {
    grid-template-columns: 1fr;
  }

  .dia-coluna {
    border-right: none;
    border-bottom: 1px solid #e0e0e0;
  }

  .dia-coluna:last-child {
    border-bottom: none;
  }
}

@media (max-width: 480px) {
  .header-main {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .status-badge {
    align-self: flex-end;
  }

  .notificacao-custom {
    left: 10px;
    right: 10px;
    top: 10px;
  }
}

/* ✅ ANIMAÇÕES */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.planejamento-semanal {
  animation: fadeIn 0.5s ease-out;
}

/* ✅ ESTADOS DE HOVER E FOCUS */
.v-btn:hover {
  transform: translateY(-2px);
  transition: all 0.3s ease;
}

.v-card:hover {
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  transition: all 0.3s ease;
}

/* ✅ SCROLLBAR CUSTOMIZADA */
.ordens-disponiveis-container::-webkit-scrollbar {
  width: 8px;
}

.ordens-disponiveis-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.ordens-disponiveis-container::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
}

.ordens-disponiveis-container::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* ✅ ESTILOS PARA DIALOG DE FILTROS AVANÇADOS */
.filtros-dialog-card {
  max-width: 800px !important;
  margin: 0 auto;
  height: 100vh;
  display: flex;
  flex-direction: column;
}

.filtros-container {
  max-width: 100%;
  margin: 0 auto;
}

.filtro-item {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 16px;
  background: #fafafa;
  transition: all 0.3s ease;
}

.filtro-item:hover {
  border-color: #1976d2;
  background: #f3f7ff;
}

.filtro-titulo {
  font-weight: 600;
  color: #1976d2;
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

/* Responsivo para mobile */
@media (max-width: 768px) {
  .filtros-dialog-card {
    max-width: 100% !important;
    margin: 0;
  }
  
  .filtros-container {
    padding: 0 8px;
  }
  
  .filtro-item {
    padding: 12px;
  }
}

/* ✅ ESTADOS DE LOADING */
.v-progress-circular {
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
  100% {
    opacity: 1;
  }
}

/* ✅ MELHORIAS VISUAIS */
.v-chip {
  font-weight: 600;
  letter-spacing: 0.5px;
}

.v-card-title {
  font-weight: 700;
}

.v-card-subtitle {
  color: #666;
}

/* ✅ ESTADOS DE ERRO */
.v-text-field--error .v-input__control {
  animation: shake 0.5s ease-in-out;
}

@keyframes shake {
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-5px); }
  75% { transform: translateX(5px); }
}

/* ✅ TRANSIÇÕES SUAVES */
* {
  transition: all 0.3s ease;
}

.v-dialog {
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
}

/* ✅ MELHORIAS DE ACESSIBILIDADE */
.v-btn:focus {
  outline: 2px solid #1976d2;
  outline-offset: 2px;
}

.setor-card-clean:focus {
  outline: 2px solid #1976d2;
  outline-offset: 2px;
}

/* ✅ DARK MODE SUPPORT (se necessário) */
@media (prefers-color-scheme: dark) {
  .planejamento-semanal {
    background: #121212;
    color: #ffffff;
  }
  
  .v-card {
    background: #1e1e1e !important;
    color: #ffffff !important;
  }
  
  .dia-header {
    background: #2d2d2d;
    color: #ffffff;
  }
}
</style>